/**
 * Client de l'API de la vie du foyer — L7.
 *
 * **Aucun chemin de ce module n'entre dans la liste blanche du service worker** (invariant 17) :
 * tous portent un montant, une identité, ou l'organisation intime d'un logement.
 *
 * **Les montants arrivent en nombres, jamais en chaînes formatées** (règle 9 du §6 de la conception
 * L10). Le formatage se fait à l'affichage, par `Intl` — un module d'argent formaté à la main est
 * un module faux en arabe.
 *
 * **La liste de courses passe par `lireSiModifie`** et non par `appeler` : c'est le contrat de
 * fraîcheur de D29 (conception L7, §3.4), et c'est lui, pas une connexion persistante, qui livre le
 * « temps réel » du §11.3.
 */

import { appeler, lireSiModifie } from './api'

export type StatutFoyer = 'Forming' | 'Active' | 'Dissolved'
export type RoleMembre = 'Member' | 'Referent'
export type StatutMembre = 'Active' | 'NoticeGiven' | 'Left' | 'Removed'
export type Recurrence = 'None' | 'Daily' | 'Weekly' | 'BiWeekly' | 'Monthly'
export type StrategieTache = 'Rotation' | 'Fixed' | 'Volunteer'
export type StatutOccurrence = 'Pending' | 'Done' | 'Skipped' | 'Overdue' | 'Reassigned' | 'Cancelled'
export type MethodeRepartition = 'Equal' | 'Shares' | 'Percentage' | 'ExactAmounts'
export type StatutReglement = 'Proposed' | 'Confirmed' | 'Cancelled'
export type TypeEvenement = 'GuestStay' | 'OwnerVisit' | 'MeterReading' | 'Maintenance' | 'Other'
export type TypeCompteur = 'Water' | 'Electricity' | 'Gas'
export type StatutDepart = 'Announced' | 'RelistDrafted' | 'ReplacementFound' | 'Completed' | 'Cancelled'
export type MoyenReglement = 'Cash' | 'BankTransfer' | 'Cheque' | 'Other'

// ---------------------------------------------------------------------------
// Foyer
// ---------------------------------------------------------------------------

export interface FoyerResume {
  id: string
  propertyId: string
  name: string
  status: StatutFoyer
  activeMemberCount: number
  gamificationEnabled: boolean
}

export interface MembreFoyer {
  memberId: string
  userId: string
  displayName: string
  unitId: string | null
  unitLabel: string | null
  role: RoleMembre
  status: StatutMembre
  joinedOn: string
  leftOn: string | null
  rentShare: number | null
}

export interface Foyer {
  id: string
  propertyId: string
  name: string
  status: StatutFoyer
  genderPolicy: string
  maxMembers: number
  gamificationEnabled: boolean
  noticePeriodDays: number
  /** Le fil du foyer, créé déverrouillé à la signature du bail (conception L7, §1.8). */
  threadConversationId: string | null
  currencyCode: string
  /** Le foyer naît à la signature du premier bail : c'est cette date. */
  createdAt: string
  members: MembreFoyer[]
}

export function mesFoyers(): Promise<FoyerResume[]> {
  return appeler<FoyerResume[]>('/me/households')
}

export function foyer(id: string): Promise<Foyer> {
  return appeler<Foyer>(`/households/${id}`)
}

export function reglerFoyer(
  id: string,
  reglages: { name?: string; gamificationEnabled?: boolean; noticePeriodDays?: number },
): Promise<void> {
  return appeler<void>(`/households/${id}/settings`, {
    method: 'PUT',
    body: JSON.stringify(reglages),
  })
}

// ---------------------------------------------------------------------------
// Tâches
// ---------------------------------------------------------------------------

export interface Tache {
  id: string
  title: string
  description: string | null
  effortWeight: number
  recurrence: Recurrence
  recurrenceInterval: number
  daysOfWeekMask: number | null
  dayOfMonth: number | null
  strategy: StrategieTache
  fixedAssigneeUserId: string | null
  requiresPhotoProof: boolean
  isActive: boolean
  startsOn: string
}

export interface Occurrence {
  id: string
  choreId: string
  choreTitle: string
  dueOn: string
  assignedToUserId: string | null
  assignedToDisplayName: string | null
  status: StatutOccurrence
  effortPoints: number
  requiresPhotoProof: boolean
  proofMediaId: string | null
  completedAt: string | null
  completedByUserId: string | null
  /**
   * Les membres sautés pour absence à l'attribution.
   *
   * C'est ce qui permet de répondre à « pourquoi encore moi ? » autrement que par « c'est
   * l'algorithme » — l'explicabilité de la rotation voyage jusqu'à l'écran.
   */
  skippedForAbsence: string[]
}

export interface Planning {
  householdId: string
  from: string
  to: string
  chores: Tache[]
  occurrences: Occurrence[]
}

export interface RepartitionMembre {
  userId: string
  displayName: string
  points: number
  completedCount: number
  currentStreak: number
  sharePercent: number
}

/**
 * Le graphe de répartition — **tout le monde, jamais une personne**.
 *
 * Conception L7, §1.4-c : la spec écrit « X a réalisé 55 % » et ajoute « sans jugement ». Les deux
 * ne tiennent pas ensemble dans une phrase affichée à quatre personnes. Ce contrat ne sait pas
 * porter une seule ligne.
 */
export interface Repartition {
  householdId: string
  from: string
  to: string
  gamificationEnabled: boolean
  isImbalanced: boolean
  members: RepartitionMembre[]
}

export function planning(householdId: string, du: string, au: string): Promise<Planning> {
  return appeler<Planning>(`/households/${householdId}/chores?from=${du}&to=${au}`)
}

export function creerTache(householdId: string, tache: Omit<Tache, 'id' | 'isActive'>): Promise<{ id: string }> {
  return appeler<{ id: string }>(`/households/${householdId}/chores`, {
    method: 'POST',
    body: JSON.stringify(tache),
  })
}

export function archiverTache(choreId: string): Promise<void> {
  return appeler<void>(`/chores/${choreId}/archive`, { method: 'POST' })
}

export function repartition(householdId: string): Promise<Repartition> {
  return appeler<Repartition>(`/households/${householdId}/chore-balance`)
}

export function terminerOccurrence(occurrenceId: string, proofMediaId: string | null = null): Promise<void> {
  return appeler<void>(`/chore-occurrences/${occurrenceId}/complete`, {
    method: 'POST',
    body: JSON.stringify({ proofMediaId }),
  })
}

export function sauterOccurrence(occurrenceId: string, reason: string | null = null): Promise<void> {
  return appeler<void>(`/chore-occurrences/${occurrenceId}/skip`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  })
}

export function reattribuerOccurrence(occurrenceId: string, toUserId: string): Promise<void> {
  return appeler<void>(`/chore-occurrences/${occurrenceId}/reassign`, {
    method: 'POST',
    body: JSON.stringify({ toUserId }),
  })
}

export function declarerAbsence(
  householdId: string,
  startsOn: string,
  endsOn: string,
  reason: string | null,
): Promise<{ id: string }> {
  return appeler<{ id: string }>(`/households/${householdId}/absences`, {
    method: 'POST',
    body: JSON.stringify({ startsOn, endsOn, reason }),
  })
}

export function annulerAbsence(absenceId: string): Promise<void> {
  return appeler<void>(`/absences/${absenceId}/cancel`, { method: 'POST' })
}

// ---------------------------------------------------------------------------
// Dépenses, soldes, remboursements
// ---------------------------------------------------------------------------

export interface PartDepense {
  userId: string
  displayName: string
  amount: number
  shares: number | null
  percentage: number | null
}

export interface Depense {
  id: string
  householdId: string
  paidByUserId: string
  paidByDisplayName: string
  title: string
  categoryCode: string
  amount: number
  currencyCode: string
  spentOn: string
  receiptMediaId: string | null
  splitMethod: MethodeRepartition
  /** Brouillon engendré par une récurrence à montant variable : il n'entre dans aucun solde. */
  needsAmountConfirmation: boolean
  shares: PartDepense[]
}

export interface SoldeMembre {
  userId: string
  displayName: string
  paid: number
  owed: number
  net: number
  currencyCode: string
}

/**
 * Un remboursement proposé, **avec les deux soldes qui le justifient**.
 *
 * Conception L7, §1.6 : « tu dois 240,00 MAD à Youssef » ne suffit pas quand il s'agit de l'argent
 * de quelqu'un. Le chiffre doit se défendre ligne à ligne, et se contester sans passer par nous.
 */
export interface TransfertPropose {
  fromUserId: string
  fromDisplayName: string
  toUserId: string
  toDisplayName: string
  amount: number
  currencyCode: string
  fromNetBalance: number
  toNetBalance: number
}

export interface Reglement {
  id: string
  fromUserId: string
  fromDisplayName: string
  toUserId: string
  toDisplayName: string
  amount: number
  currencyCode: string
  settledOn: string
  status: StatutReglement
  declaredByUserId: string
  confirmedByUserId: string | null
  note: string | null
}

export interface Soldes {
  householdId: string
  currencyCode: string
  balances: SoldeMembre[]
  suggestedTransfers: TransfertPropose[]
  pendingSettlements: Reglement[]
}

export interface PageDepenses {
  items: Depense[]
  page: number
  size: number
  total: number
}

export function depenses(householdId: string, page = 1): Promise<PageDepenses> {
  return appeler<PageDepenses>(`/households/${householdId}/expenses?page=${page}&size=25`)
}

export function ajouterDepense(
  householdId: string,
  depense: {
    title: string
    categoryCode: string
    amount: number
    spentOn: string
    splitMethod: MethodeRepartition
    receiptMediaId?: string | null
    shares?: { userId: string; shares?: number | null; percentage?: number | null; exactAmount?: number | null }[]
  },
): Promise<{ id: string }> {
  return appeler<{ id: string }>(`/households/${householdId}/expenses`, {
    method: 'POST',
    body: JSON.stringify(depense),
  })
}

/** Effacement **logique** : aucun rôle n'a le `DELETE` physique sur une dépense. */
export function retirerDepense(expenseId: string): Promise<void> {
  return appeler<void>(`/expenses/${expenseId}/remove`, { method: 'POST' })
}

export function soldes(householdId: string): Promise<Soldes> {
  return appeler<Soldes>(`/households/${householdId}/balance`)
}

export function proposerReglement(
  householdId: string,
  reglement: {
    fromUserId: string
    toUserId: string
    amount: number
    method: MoyenReglement
    settledOn: string
    note?: string | null
  },
): Promise<{ id: string }> {
  return appeler<{ id: string }>(`/households/${householdId}/settlements`, {
    method: 'POST',
    body: JSON.stringify(reglement),
  })
}

export function confirmerReglement(settlementId: string): Promise<void> {
  return appeler<void>(`/settlements/${settlementId}/confirm`, { method: 'POST' })
}

export function annulerReglement(settlementId: string, reasonCode: string): Promise<void> {
  return appeler<void>(`/settlements/${settlementId}/cancel`, {
    method: 'POST',
    body: JSON.stringify({ reasonCode }),
  })
}

// ---------------------------------------------------------------------------
// Courses — le contrat de fraîcheur
// ---------------------------------------------------------------------------

export interface ArticleCourses {
  id: string
  label: string
  quantity: string | null
  addedByUserId: string
  isChecked: boolean
  checkedByUserId: string | null
  checkedAt: string | null
  sortOrder: number
}

export interface ListeCourses {
  id: string
  householdId: string
  name: string
  /** Entier monotone. C'est lui qui porte le « temps réel » : `ETag` à l'aller, `304` au retour. */
  version: number
  items: ArticleCourses[]
}

export function listesCourses(householdId: string): Promise<ListeCourses[]> {
  return appeler<ListeCourses[]>(`/households/${householdId}/shopping-lists`)
}

/**
 * Relit la liste **seulement si elle a changé**.
 *
 * Renvoie `null` quand le serveur répond `304`. C'est ce qui rend acceptable d'interroger toutes
 * les quatre secondes pendant les courses : une liste inchangée coûte quelques centaines d'octets.
 */
export function listeCoursesSiModifiee(listId: string, versionConnue: number | null): Promise<ListeCourses | null> {
  return lireSiModifie<ListeCourses>(`/shopping-lists/${listId}`, versionConnue)
}

export function ajouterArticle(listId: string, label: string, quantity: string | null): Promise<{ version: number }> {
  return appeler<{ version: number }>(`/shopping-lists/${listId}/items`, {
    method: 'POST',
    body: JSON.stringify({ label, quantity }),
  })
}

/** Cocher : **dernier écrivain gagne**. C'est un booléen, personne ne perd d'information. */
export function cocherArticle(itemId: string, checked: boolean): Promise<{ version: number }> {
  return appeler<{ version: number }>(`/shopping-items/${itemId}/check`, {
    method: 'POST',
    body: JSON.stringify({ checked }),
  })
}

/** Le seul `DELETE` du lot : un article rayé n'est la preuve de rien. */
export function retirerArticle(itemId: string): Promise<{ version: number }> {
  return appeler<{ version: number }>(`/shopping-items/${itemId}`, { method: 'DELETE' })
}

export function convertirEnDepense(
  listId: string,
  conversion: { amount: number; categoryCode: string; spentOn: string; receiptMediaId?: string | null },
): Promise<{ id: string }> {
  return appeler<{ id: string }>(`/shopping-lists/${listId}/to-expense`, {
    method: 'POST',
    body: JSON.stringify(conversion),
  })
}

// ---------------------------------------------------------------------------
// Agenda, compteurs, règlement intérieur
// ---------------------------------------------------------------------------

export interface EvenementFoyer {
  id: string
  kind: TypeEvenement
  title: string
  note: string | null
  startsAt: string
  endsAt: string
  allDay: boolean
  visibleToOwner: boolean
  createdByUserId: string
  createdByDisplayName: string
}

export interface AbsenceFoyer {
  id: string
  userId: string
  displayName: string
  startsOn: string
  endsOn: string
  reason: string | null
}

/** Deux sources réunies à la lecture : `household_event` et `member_absence` (§3.3). */
export interface Agenda {
  householdId: string
  from: string
  to: string
  events: EvenementFoyer[]
  absences: AbsenceFoyer[]
}

export function agenda(householdId: string): Promise<Agenda> {
  return appeler<Agenda>(`/households/${householdId}/agenda`)
}

export function ajouterEvenement(
  householdId: string,
  evenement: {
    kind: TypeEvenement
    title: string
    note: string | null
    startsAt: string
    endsAt: string
    allDay: boolean
    visibleToOwner: boolean
  },
): Promise<{ id: string }> {
  return appeler<{ id: string }>(`/households/${householdId}/events`, {
    method: 'POST',
    body: JSON.stringify(evenement),
  })
}

export function annulerEvenement(eventId: string): Promise<void> {
  return appeler<void>(`/events/${eventId}/cancel`, { method: 'POST' })
}

export interface ReleveCompteur {
  id: string
  kind: TypeCompteur
  value: number
  consumption: number | null
  readOn: string
  readByUserId: string
  photoMediaId: string | null
}

export function releves(householdId: string): Promise<ReleveCompteur[]> {
  return appeler<ReleveCompteur[]>(`/households/${householdId}/meter-readings`)
}

export function releverCompteur(
  householdId: string,
  releve: { kind: TypeCompteur; value: number; readOn: string; photoMediaId?: string | null },
): Promise<{ id: string }> {
  return appeler<{ id: string }>(`/households/${householdId}/meter-readings`, {
    method: 'POST',
    body: JSON.stringify(releve),
  })
}

export interface AcceptationReglement {
  userId: string
  displayName: string
  /** Nul tant que le membre n'a pas accepté : « qui n'a pas encore » est une lecture. */
  acceptedAt: string | null
}

export interface VersionReglement {
  id: string
  version: number
  language: string
  publishedAt: string
  publishedByUserId: string | null
}

/** Le règlement intérieur. **« Accepté », jamais « signé »** — conception L7, §1.9. */
export interface ReglementInterieur {
  householdId: string
  rulesId: string | null
  version: number
  content: string | null
  language: string
  publishedAt: string | null
  publishedByUserId: string | null
  acceptedByMe: boolean
  acceptances: AcceptationReglement[]
  history: VersionReglement[]
}

export function reglementInterieur(householdId: string): Promise<ReglementInterieur> {
  return appeler<ReglementInterieur>(`/households/${householdId}/house-rules`)
}

export function publierReglement(householdId: string, content: string, language: string): Promise<{ id: string }> {
  return appeler<{ id: string }>(`/households/${householdId}/house-rules`, {
    method: 'POST',
    body: JSON.stringify({ content, language }),
  })
}

export function accepterReglement(rulesId: string): Promise<void> {
  return appeler<void>(`/house-rules/${rulesId}/accept`, { method: 'POST' })
}

// ---------------------------------------------------------------------------
// Départ et remplacement
// ---------------------------------------------------------------------------

export interface Depart {
  id: string
  householdId: string
  memberUserId: string
  memberDisplayName: string
  unitId: string | null
  announcedAt: string
  effectiveOn: string
  replacementRequired: boolean
  status: StatutDepart
  /** Le **brouillon** d'annonce créé pour la chambre libérée. Le propriétaire seul le publie. */
  relistedListingId: string | null
  replacementApplicationId: string | null
}

export function departs(householdId: string): Promise<Depart[]> {
  return appeler<Depart[]>(`/households/${householdId}/departures`)
}

/**
 * Annonce **son propre** départ.
 *
 * Aucun champ ne désigne quelqu'un d'autre, et c'est structurel (conception L7, §1.10-a) : un
 * bailleur résilie un bail, des colocataires n'expulsent personne.
 */
export function annoncerDepart(
  householdId: string,
  effectiveOn: string,
  replacementRequired: boolean,
): Promise<{ id: string }> {
  return appeler<{ id: string }>(`/households/${householdId}/departures`, {
    method: 'POST',
    body: JSON.stringify({ effectiveOn, replacementRequired }),
  })
}

export function annulerDepart(departureId: string, reasonCode: string): Promise<void> {
  return appeler<void>(`/departures/${departureId}/cancel`, {
    method: 'POST',
    body: JSON.stringify({ reasonCode }),
  })
}
